
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProject } from "@/contexts/ProjectContext";
import NavBar from "@/components/NavBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Calendar, ListTodo } from "lucide-react";
import { GanttChartView } from "@/components/gantt/GanttChartView";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useIsMobile } from "@/hooks/use-mobile";

export default function GanttChart() {
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const [activeView, setActiveView] = useState<"timeline" | "activities">("timeline");
  const isMobile = useIsMobile();
  
  if (!currentProject) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <div className="container py-8 flex-1 flex flex-col items-center justify-center">
          <h1 className="text-2xl font-bold mb-4">No project selected</h1>
          <Button onClick={() => navigate("/projects-selector")}>
            Select a Project
          </Button>
        </div>
      </div>
    );
  }

  // Calculate timeline based on project dates
  const startDate = currentProject.startDate;
  const endDate = currentProject.targetCompletionDate;
  
  // Calculate total project duration in days
  const projectDuration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  // Calculate timeline scale
  const timelineScale = 100 / projectDuration;
  
  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="container py-6 flex-1">
        <div className="flex flex-col sm:flex-row sm:items-center mb-6 gap-2">
          <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full sm:w-auto">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold sm:ml-4 truncate">Programme Gantt Chart</h1>
        </div>
        
        <Card className="mb-8">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <CardTitle className="truncate">Project Timeline</CardTitle>
              <div className="flex flex-col sm:flex-row sm:items-center text-sm gap-1 sm:gap-2">
                <span className="flex items-center truncate">
                  <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap truncate">
                    {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                  </span>
                </span>
                <span className="hidden sm:inline-block">|</span>
                <span className="truncate">{projectDuration} days</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="overflow-hidden">
            <div className="flex justify-center mb-6">
              <ToggleGroup type="single" value={activeView} onValueChange={(value) => value && setActiveView(value as "timeline" | "activities")}>
                <ToggleGroupItem value="timeline" aria-label="View timeline">
                  <Calendar className="h-4 w-4 mr-2" /> Timeline
                </ToggleGroupItem>
                <ToggleGroupItem value="activities" aria-label="View activities">
                  <ListTodo className="h-4 w-4 mr-2" /> Activities
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div className="h-[300px] sm:h-[400px] overflow-y-auto overflow-x-auto">
              <GanttChartView 
                viewMode={activeView === "timeline" ? "stage" : "activity"}
                currentProject={currentProject}
                startDate={startDate}
                endDate={endDate}
                timelineScale={timelineScale}
              />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
